import React, { useEffect } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import Button from "../Button";
import BackButton from "../common/BackButton";
import { getAreasById, getCinemaEntities, getStates, getStatesById } from "../../reducers";
import { filterCinemasNearLatLng, PositionType } from "../../utils/location";
import { CinemaType } from "../../types/entities";
import { areaSelectorSelectArea } from "../../actions/creators/ui";
import BottomPanel from "../BottomPanel";
import { getUserFavoriteCinemasIds } from "../../reducers/user";

const Container = styled.div<{ open: boolean }>`
    position: fixed;
    top: 0;
    background: var(--page-bg);
    width: 100%;
    height: 100%;
    z-index: 1000;
    right: ${(props) => (props.open ? '0' : '-100%')};
    transition: right 0.3s ease-in-out;
`;

const ResultsTitle = styled.p`
    font-size: 1.25em;
    font-weight: bold;
    margin-bottom: 1rem;
`;

const ResultItem = styled.div<{ isFavorite: boolean }>`
    align-items: center;
    cursor: pointer;
    display: flex;
    padding: 0.5rem 0;

    i {
        color: ${(props) => (props.isFavorite ? 'var(--primary)' : 'var(--text-primary)')};
        margin-right: 0.75rem;
    }
`;

interface Props {
    areasById: { [key: string]: { id: number, name: string } };
    cinemaConcept: 'traditional' | 'platinum' | 'market' | 'all';
    cinemasById: { [key: number]: CinemaType };
    cinemaSelect?: boolean;
    favCinemas: number[];
    states: Array<{ id: number, name: string, areas: Array<{ id: number, name: string }> }>;
    statesById: { [key: string]: { id: number, name: string, areas: Array<{ id: number, name: string }> } };
    onAreaSelect: (type: string, id: number, args?: { [key: string]: any }) => void;
    onAdd: (cinemaId: number) => void;
    onRemove: (cinemaId: number) => void;
    onClose: () => void;
    onSave: (data: { cinemaConcept: 'traditional' | 'platinum' | 'market' | 'all' }) => void;
};

const AddFavCinemaSelector = (props: Props) => {
    const [loaded, setLoaded] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [cinemaConcept, setCinemaConcept] = React.useState<'traditional' | 'platinum' | 'market' | 'all'>(props.cinemaConcept);
    const [cinemaId, setCinemaId] = React.useState<number | undefined>();
    const [stateId, setStateId] = React.useState<number | undefined>();
    const [areaId, setAreaId] = React.useState<string>('');
    const [nearestCinema, setNearestCinema] = React.useState<CinemaType | null>(null);
    const [location, setLocation] = React.useState<PositionType | null>(null);
    const [locationError, setLocationError] = React.useState<string | null>(null);
    const [searchTerm, setSearchTerm] = React.useState('');

    useEffect(() => {
        if (!loaded) {
            setTimeout(() => {
                setLoaded(true);
                setOpen(true);
            }, 10);
        }
    }, [loaded]);

    useEffect(() => {
        if (!location) {
            return;
        }

        const nearestCinemas = filterCinemasNearLatLng(Object.values(props.cinemasById), location.lat, location.lng);
        if (!nearestCinemas.length) {
            setLocationError('No se encontraron cines cercanos a tu ubicación');
            return;
        }

        setNearestCinema(nearestCinemas[0]);
        setStateId(nearestCinemas[0].state.id);
        setAreaId('area-' + nearestCinemas[0].area.id);
    }, [location]);

    function onSave() {
        const parts = areaId.toString().split('-');
        const type = parts[0];
        const id = parseInt(parts[1], 10);
        
        props.onAreaSelect(type, id, { context: 'global' });
        props.onSave({
            cinemaConcept
        });
        props.onClose();
    }

    function onLocationRequest() {
        if (!navigator.geolocation) {
            return;
        }
        navigator.geolocation.getCurrentPosition(
            position => {
                setLocation({ lat: position.coords.latitude, lng: position.coords.longitude });
            },
            error => {
                console.error('Error getting location', error);
                setLocation(null);
                setLocationError(error.message);
            }
        );
    }

    function getCinemasResult(): Array<CinemaType> {
        let result = Object.values(props.cinemasById);
        if (areaId) {
            result = result.filter(cinema => {
                const area = areaId.split('-');
                return 'state' === area[0]
                    ? cinema.state.id === parseInt(area[1], 10)
                    : cinema.area.id === parseInt(area[1], 10);
            });
        } else if (stateId) {
            result = result.filter(cinema => cinema.state.id === stateId);
        }

        if (searchTerm && searchTerm.length >= 2) {
            result = result.filter(cinema => cinema.name.toLowerCase().includes(searchTerm.toLowerCase()));
        }

        return result;
    }

    function isCinemaFavorite(cinemaId: number) {
        return props.favCinemas.includes(cinemaId);
    }

    return <BottomPanel open onClose={props.onClose} title='Agregar cine'>
        <div className='text-left pb-3'>
            <div className='text-center'>
                {locationError && <>
                    <p className="my-5">No se pudo obtener tu ubicación. Selecciona tu {props.cinemaSelect ? 'cine' : 'zona'} manualmente:</p>
                </>}

                {(location && nearestCinema && props.areasById)
                    ? <p className="my-5">Tu zona más cercana es <strong>{props.areasById[nearestCinema.area.id].name}</strong></p>
                    : <p className="my-5">
                        Selecciona el <strong>estado o zona</strong> donde quieres ver los cines disponibles.
                        Toca el corazón para agregarlo a tus favoritos.
                    </p>}
                <Button primary block onClick={onLocationRequest}>Encontrar mi ubicación automáticamente</Button>
            </div>

            {!locationError && <p className="mt-5">O selecciona tu {props.cinemaSelect ? 'cine' : 'zona'} manualmente:</p>}
            
            <div className="form-group">
                <label>Tu estado</label>
                <select className="form-control" value={stateId} onChange={(e) => setStateId(parseInt(e.target.value, 10))}>
                    <option value="">Selecciona un estado</option>
                    {props.states.map((state) => <option key={state.id} value={state.id}>{state.name}</option>)}
                </select>
            </div>
            <div className="form-group">
                <label>Tu zona</label>
                <select className="form-control" value={areaId} onChange={(e) => setAreaId(e.target.value)}>
                    <option value="">Selecciona una zona</option>
                    {stateId && <>
                        {!props.cinemaSelect && props.statesById[stateId].areas.length > 1 && <option value={`state-${stateId}`}>Todo {props.statesById[stateId].name}</option>}
                        {props.statesById[stateId].areas.map((area) => <option key={area.id} value={`area-${area.id}`}>{area.name}</option>)}
                    </>}
                </select>
            </div>

            {/* {props.cinemaSelect && <div className="form-group">
                <label>Tu cine favorito</label>
                <select className="form-control" value={cinemaId} onChange={(e) => setCinemaId(parseInt(e.target.value, 10))} disabled={!areaId}>
                    <option value="">Selecciona un cine</option>
                    {areaId && getCinemas().map(cinema => <option key={cinema.id} value={cinema.id}>{cinema.name}</option>)}
                </select>
            </div>} */}

            {props.cinemaSelect && <div className="mt-5">
                <p>O escribe el nombre del cine que quieres agregar</p>
                <input className="form-control" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}></input>
            </div>}

            {(!!areaId || (searchTerm && searchTerm.length >= 2)) && <div className="mt-5">
                <ResultsTitle className="title-2">Resultado cines</ResultsTitle>
                {getCinemasResult().map(cinema => {
                    const isFavorite = isCinemaFavorite(cinema.id);
                    return <ResultItem key={cinema.id} isFavorite={isFavorite} onClick={() => {
                        if (isFavorite) {
                            props.onRemove(cinema.id);
                        } else {
                            props.onAdd(cinema.id);
                        }
                    }}>
                        <i className={`${isFavorite ? 'fas' : 'fal'} fa-heart`}></i>
                        {cinema.name}
                    </ResultItem>
                })}
            </div>}

            {!props.cinemaSelect && !!areaId && <div className="mt-5">
                <p>Selecciona el tipo de cine que deseas ver:</p>
                <select className="form-control" value={cinemaConcept} onChange={(e) => setCinemaConcept(e.target.value as any)}>
                    <option value="all">Todos</option>
                    <option value="traditional">Tradicional</option>
                    <option value="platinum">Platino</option>
                    <option value="market">Market</option>
                </select>
            </div>}

            {!props.cinemaSelect && <div className="mt-5">
                <Button primary block onClick={onSave} disabled={!stateId || !areaId}>Aplicar</Button>
            </div>}
        </div>
    </BottomPanel>;
}

const mapStateToProps = (state) => {
    return {
        areasById: getAreasById(state),
        cinemasById: getCinemaEntities(state),
        favCinemas: getUserFavoriteCinemasIds(state),
        states: getStates(state),
        statesById: getStatesById(state),
    }
};

const mapDispatchToProps = (dispatch) => {
    return {
        onAreaSelect: (type: string, id: number, args?: { [key: string]: any }) => dispatch(areaSelectorSelectArea(type, id, args)),
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(AddFavCinemaSelector);